import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { IconCal, IconChevronLeft, IconChevronRight } from "../icons/Index";

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS         = ["Su","Mo","Tu","We","Th","Fr","Sa"];

/* ── Token values ── */
const T = {
  bg:       "#fdfbf8",
  s50:      "#faf7f2",
  s100:     "#f0e9de",
  s200:     "#e2d5c3",
  border:   "#e5ddd3",
  dk:       "#1a1410",
  mid:      "#3d332a",
  muted:    "#7d6e61",
  light:    "#b0a090",
  teal:     "#2a9d8f",
  tealDark: "#1f8a7e",
  tealDeep: "#176b62",
  tealGlow: "rgba(42,157,143,0.18)",
  tealBg:   "rgba(42,157,143,0.08)",
  white:    "#ffffff",
  coral:    "#e07b54",
  error:    "#e05252",
};

/* ── Safe date parser ── */
const parseDate = (val) => {
  if (!val || typeof val !== "string") return null;
  let d;
  if      (/^\d{4}-\d{2}-\d{2}T/.test(val.trim()))      d = new Date(val.trim().slice(0,10)+"T00:00:00");
  else if (/^\d{4}-\d{2}-\d{2}$/.test(val.trim()))       d = new Date(val.trim()+"T00:00:00");
  else if (/^\d{2}\/\d{2}\/\d{4}$/.test(val.trim())) {
    const [mm,dd,yyyy] = val.trim().split("/");
    d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  } else return null;
  return isNaN(d.getTime()) ? null : d;
};

/* ── Digit mask helpers ── */
const toDisplay = (digits) => {
  let out = "";
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) out += "/";
    out += digits[i];
  }
  return out;
};
const caretFor = (len) => (len <= 2 ? len : len <= 4 ? len + 1 : len + 2);
const smartPad = (d) => {
  let r = d;
  if (r.length === 1 && parseInt(r[0],10) > 1)           r = "0"+r;
  if (r.length >= 2 && r.slice(0,2) === "00")             r = "01"+r.slice(2);
  if (r.length >= 2 && parseInt(r.slice(0,2),10) > 12)   r = "12"+r.slice(2);
  if (r.length === 3 && parseInt(r[2],10) > 3)            r = r.slice(0,2)+"0"+r[2];
  if (r.length >= 4 && r.slice(2,4) === "00")             r = r.slice(0,2)+"01"+r.slice(4);
  if (r.length >= 4 && parseInt(r.slice(2,4),10) > 31)   r = r.slice(0,2)+"31"+r.slice(4);
  return r;
};

/* ══════════════════════════════════════════════════════════ */
const DatePicker = ({ value, onChange, hasError, maxDate, minDate }) => {
  const today = new Date(); today.setHours(0,0,0,0);
  const maxD = parseDate(maxDate) ?? today;
  const minD = parseDate(minDate) ?? new Date(today.getFullYear()-150,0,1);

  const [open,     setOpen]    = useState(false);
  const [mode,     setMode]    = useState("days");
  const [pos,      setPos]     = useState({ top:0, left:0, width:240 });
  const [hovDay,   setHovDay]  = useState(null);
  const [hovMonth, setHovMonth]= useState(null);
  const [hovYear,  setHovYear] = useState(null);
  const [digits,   setDigits]  = useState(() => {
    const d = parseDate(value);
    if (!d) return "";
    return String(d.getMonth()+1).padStart(2,"0")+String(d.getDate()).padStart(2,"0")+String(d.getFullYear());
  });
  const [viewDate, setViewDate] = useState(() => {
    const d = parseDate(value);
    return d ? { month:d.getMonth(), year:d.getFullYear() }
             : { month:new Date().getMonth(), year:new Date().getFullYear() };
  });

  const wrapRef     = useRef(null);
  const dropRef     = useRef(null);
  const inputRef    = useRef(null);
  const yearListRef = useRef(null);

  const selectedDate = parseDate(value);

  const computePos = () => {
    if (!wrapRef.current) return;
    const r   = wrapRef.current.getBoundingClientRect();
    const dpW = Math.max(r.width, 240);
    const dpH = 320;

    const above = (window.innerHeight - r.bottom) < dpH && r.top >= dpH;
    let top  = above ? r.top - dpH - 4 : r.bottom + 4;
    let left = r.left;

    if (top + dpH > window.innerHeight - 8) top  = window.innerHeight - dpH - 8;
    if (top < 8)                            top  = 8;
    if (left + dpW > window.innerWidth - 8) left = window.innerWidth - dpW - 8;
    if (left < 8)                           left = 8;

    setPos({ top, left, width: dpW });
  };

  /* Sync when value changes externally */
  useEffect(() => {
    const d = parseDate(value);
    if (d) {
      setDigits(String(d.getMonth()+1).padStart(2,"0")+String(d.getDate()).padStart(2,"0")+String(d.getFullYear()));
      setViewDate({ month:d.getMonth(), year:d.getFullYear() });
    } else setDigits("");
  }, [value]);

  /* Caret at end */
  useEffect(() => {
    const el = inputRef.current;
    if (!el || document.activeElement !== el) return;
    const p = caretFor(digits.length);
    requestAnimationFrame(() => el.setSelectionRange(p, p));
  }, [digits]);

  /* Scroll selected year into view */
  useEffect(() => {
    if (mode === "years" && yearListRef.current) {
      const sel = yearListRef.current.querySelector("[data-selected='true']");
      if (sel) requestAnimationFrame(() => sel.scrollIntoView({ block:"center", behavior:"instant" }));
    }
  }, [mode]);

  /* Recompute on scroll/resize while open */
  useEffect(() => {
    if (!open) return;
    const update = () => computePos();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]); // eslint-disable-line

  /* Close on outside click */
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (dropRef.current  && !dropRef.current.contains(e.target) &&
          wrapRef.current  && !wrapRef.current.contains(e.target))
        { setOpen(false); setMode("days"); }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  /* ── Commit ── */
  const tryCommit = (d) => {
    if (d.length < 8) { onChange(""); return; }
    const mm=d.slice(0,2), dd=d.slice(2,4), yyyy=d.slice(4,8);
    const mo=parseInt(mm,10), dy=parseInt(dd,10), yr=parseInt(yyyy,10);
    if (mo>=1&&mo<=12&&dy>=1&&dy<=31&&yr>=1900) {
      const dt = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
      if (dt.getFullYear()===yr&&dt.getMonth()===mo-1&&dt.getDate()===dy&&dt<=maxD&&dt>=minD) {
        onChange(`${yyyy}-${mm}-${dd}`);
        setViewDate({ month:mo-1, year:yr });
        return;
      }
    }
    onChange("");
  };

  /* ── Keys ── */
  const handleKeyDown = (e) => {
    if (e.key==="Escape"||e.key==="Tab") { setOpen(false); setMode("days"); return; }
    if (e.key==="ArrowDown"&&!open) { e.preventDefault(); openCal(); return; }

    if (e.key==="Backspace") {
      e.preventDefault();
      setDigits(p => {
        const n = p.slice(0, -1);
        if (n.length === 8) tryCommit(n);
        else onChange("");
        return n;
      });
      return;
    }

    if (e.key==="Delete") { e.preventDefault(); setDigits(""); onChange(""); return; }

    // Allow Ctrl+V / Cmd+V through so the paste event can fire
    if ((e.ctrlKey || e.metaKey) && e.key === "v") return;

    if (!/^\d$/.test(e.key)) { e.preventDefault(); return; }
    e.preventDefault();
    setDigits(p => {
      if (p.length >= 8) return p;
      const n = smartPad(p + e.key);
      tryCommit(n);
      return n;
    });
  };

  const handleChange = (e) => e.preventDefault();

  /* ── Paste ── */
  const handlePaste = (e) => {
    e.preventDefault();
    const p = (e.clipboardData.getData("text") || "").trim();
    let mm, dd, yyyy;
    const iso = p.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
    const mdy = p.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    const raw = p.replace(/\D/g, "");
    if      (iso)            { [,yyyy,mm,dd] = iso; mm=mm.padStart(2,"0"); dd=dd.padStart(2,"0"); }
    else if (mdy)            { [,mm,dd,yyyy] = mdy; mm=mm.padStart(2,"0"); dd=dd.padStart(2,"0"); }
    else if (raw.length===8) { mm=raw.slice(0,2); dd=raw.slice(2,4); yyyy=raw.slice(4,8); }
    else if (raw.length > 0) {
      // Partial paste: feed up to 8 digits through smartPad
      const clamped = raw.slice(0, 8);
      const nd = smartPad(clamped);
      setDigits(nd);
      tryCommit(nd);
      return;
    }
    if (mm && dd && yyyy) {
      const nd = smartPad(mm + dd + yyyy);
      setDigits(nd);
      tryCommit(nd);
    }
  };

  /* ── Open calendar ── */
  const openCal = () => {
    computePos();
    setOpen(true);
  };

  /* ── Grid ── */
  const isDisabled = (day) => {
    const d = new Date(viewDate.year, viewDate.month, day);
    d.setHours(0,0,0,0);
    return d > maxD || d < minD;
  };
  const firstDay    = new Date(viewDate.year, viewDate.month, 1).getDay();
  const daysInMonth = new Date(viewDate.year, viewDate.month+1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectDay = (day) => {
    if (!day || isDisabled(day)) return;
    const d  = new Date(viewDate.year, viewDate.month, day);
    const mm = String(d.getMonth()+1).padStart(2,"0");
    const dd = String(d.getDate()).padStart(2,"0");
    onChange(`${d.getFullYear()}-${mm}-${dd}`);
    setOpen(false); setMode("days");
  };
  const prevMonth = () => setViewDate(v => ({ month:v.month===0?11:v.month-1, year:v.month===0?v.year-1:v.year }));
  const nextMonth = () => setViewDate(v => ({ month:v.month===11?0:v.month+1, year:v.month===11?v.year+1:v.year }));

  const yearList = [];
  for (let y = maxD.getFullYear(); y >= minD.getFullYear(); y--) yearList.push(y);

  /* ── Hover states ── */
  const [hovPrev,   setHovPrev]   = useState(false);
  const [hovNext,   setHovNext]   = useState(false);
  const [hovMon,    setHovMon]    = useState(false);
  const [hovYr,     setHovYr]     = useState(false);
  const [hovBack,   setHovBack]   = useState(false);
  const [hovYrPill, setHovYrPill] = useState(false);

  /* ══ Styles ══ */
  const S = {
    drop: {
      position:     "fixed",
      top:          pos.top,
      left:         pos.left,
      width:        pos.width,
      zIndex:       99999,
      background:   T.white,
      border:       `1.5px solid ${T.border}`,
      borderRadius: 12,
      boxShadow:    "0 8px 32px rgba(26,20,16,0.14),0 2px 8px rgba(26,20,16,0.07)",
      overflow:     "hidden",
      fontFamily:   "inherit",
      animation:    "pd-dp-fadein 0.16s cubic-bezier(0.22,0.9,0.34,1) both",
    },
    header: {
      display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"10px 10px 8px", borderBottom:`1px solid ${T.border}`,
      background:T.s50, gap:4,
    },
    headerSub: {
      display:"flex", alignItems:"center",
      padding:"10px 10px 8px", borderBottom:`1px solid ${T.border}`,
      background:T.s50, gap:8,
    },
    navBtn: (hov) => ({
      width:28, height:28, borderRadius:7, border:`1.5px solid ${T.border}`,
      background: hov ? T.s100 : T.white,
      color: hov ? T.teal : T.mid,
      cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
      transition:"all 0.15s", flexShrink:0, padding:0,
    }),
    pill: (hov) => ({
      display:"inline-flex", alignItems:"center", gap:3,
      padding:"3px 9px", borderRadius:999,
      border:`1.5px solid ${hov ? T.teal : T.border}`,
      background: hov ? T.tealBg : T.s50,
      color: hov ? T.tealDark : T.dk,
      fontSize:12, fontWeight:700, fontFamily:"'Montserrat',sans-serif",
      letterSpacing:"0.04em", cursor:"pointer", lineHeight:1.4,
      transition:"all 0.15s",
    }),
    backBtn: (hov) => ({
      display:"inline-flex", alignItems:"center", gap:3,
      padding:"3px 8px", border:"none",
      background: hov ? T.tealBg : "transparent",
      color: hov ? T.teal : T.tealDark,
      fontSize:11.5, fontWeight:700, fontFamily:"'Montserrat',sans-serif",
      letterSpacing:"0.04em", cursor:"pointer", borderRadius:6,
      transition:"all 0.15s", flexShrink:0,
    }),
    dayNames: {
      display:"grid", gridTemplateColumns:"repeat(7,1fr)",
      padding:"6px 10px 2px", gap:1,
    },
    dayName: {
      textAlign:"center", fontSize:9, fontWeight:700,
      fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.08em",
      textTransform:"uppercase", color:T.muted, padding:"2px 0",
    },
    grid: {
      display:"grid", gridTemplateColumns:"repeat(7,1fr)",
      padding:"2px 10px 6px", gap:2,
    },
    cell: (sel,tod,dis,hov) => ({
      aspectRatio:1, borderRadius:6, border:"none",
      background: sel ? `linear-gradient(135deg,${T.tealDark},${T.teal})` : hov&&!dis ? T.s100 : "none",
      color: sel ? T.white : dis ? T.light : hov&&!dis ? T.tealDark : T.dk,
      fontSize:11.5, fontWeight: sel||tod ? 700 : 500,
      cursor: dis ? "not-allowed" : "pointer",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:0, minWidth:0, fontFamily:"inherit",
      outline: tod&&!sel ? `1.5px solid ${T.teal}` : "none",
      opacity: dis ? 0.4 : 1,
      transition:"background 0.12s,color 0.12s",
      boxShadow: sel ? `0 3px 10px ${T.tealGlow}` : "none",
    }),
    monthGrid: {
      display:"grid", gridTemplateColumns:"repeat(4,1fr)",
      gap:4, padding:"10px 10px 12px",
    },
    monthCell: (sel,hov) => ({
      borderRadius:8,
      border:`1.5px solid ${sel ? "transparent" : hov ? T.s200 : "transparent"}`,
      background: sel ? `linear-gradient(135deg,${T.tealDark},${T.teal})` : hov ? T.s100 : T.s50,
      color: sel ? T.white : hov ? T.tealDark : T.mid,
      fontSize:11.5, fontWeight:600, fontFamily:"'Montserrat',sans-serif",
      letterSpacing:"0.04em", padding:"7px 0", cursor:"pointer",
      textAlign:"center", transition:"all 0.13s",
    }),
    yearList: {
      maxHeight:220, overflowY:"auto", padding:"8px",
      display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:4,
      scrollbarWidth:"thin",
    },
    yearItem: (sel,hov) => ({
      border: `1.5px solid ${sel ? "transparent" : hov ? T.teal : "transparent"}`,
      borderRadius:7, padding:"7px 4px", textAlign:"center",
      fontSize:12.5, fontWeight: sel ? 700 : 500,
      color: sel ? T.white : hov ? T.tealDark : T.mid,
      background: sel ? `linear-gradient(135deg,${T.tealDark},${T.teal})` : hov ? T.tealBg : "none",
      cursor:"pointer", transition:"all 0.13s", fontFamily:"inherit",
      boxShadow: sel ? `0 2px 8px ${T.tealGlow}` : "none",
    }),
    subLabel: {
      fontSize:12, fontWeight:700, fontFamily:"'Montserrat',sans-serif",
      letterSpacing:"0.06em", color:T.dk,
      marginLeft:"auto", marginRight:"auto",
    },
  };

  /* ── Portal dropdown ── */
  const dropdown = open && ReactDOM.createPortal(
    <>
      <style>{`@keyframes pd-dp-fadein{from{opacity:0;transform:translateY(-6px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
      <div ref={dropRef} style={S.drop}>

        {/* DAYS */}
        {mode === "days" && <>
          <div style={S.header}>
            <button type="button" style={S.navBtn(hovPrev)}
              onMouseEnter={()=>setHovPrev(true)} onMouseLeave={()=>setHovPrev(false)}
              onClick={prevMonth}><IconChevronLeft /></button>

            <div style={{display:"flex",alignItems:"center",gap:4,flex:1,justifyContent:"center"}}>
              <button type="button" style={S.pill(hovMon)}
                onMouseEnter={()=>setHovMon(true)} onMouseLeave={()=>setHovMon(false)}
                onClick={()=>setMode("months")}>
                {MONTHS_SHORT[viewDate.month]} <span style={{fontSize:8,opacity:.45}}>▾</span>
              </button>
              <button type="button" style={S.pill(hovYr)}
                onMouseEnter={()=>setHovYr(true)} onMouseLeave={()=>setHovYr(false)}
                onClick={()=>setMode("years")}>
                {viewDate.year} <span style={{fontSize:8,opacity:.45}}>▾</span>
              </button>
            </div>

            <button type="button" style={S.navBtn(hovNext)}
              onMouseEnter={()=>setHovNext(true)} onMouseLeave={()=>setHovNext(false)}
              onClick={nextMonth}><IconChevronRight /></button>
          </div>

          <div style={S.dayNames}>
            {DAYS.map(d => <span key={d} style={S.dayName}>{d}</span>)}
          </div>

          <div style={S.grid}>
            {cells.map((day,i) => {
              if (!day) return <span key={`e${i}`} style={{aspectRatio:1}} />;
              const dt  = new Date(viewDate.year, viewDate.month, day); dt.setHours(0,0,0,0);
              const dis = isDisabled(day);
              const sel = selectedDate &&
                selectedDate.getDate()    === day &&
                selectedDate.getMonth()   === viewDate.month &&
                selectedDate.getFullYear() === viewDate.year;
              const tod = dt.getTime() === today.getTime();
              const hov = hovDay === i;
              return (
                <button key={day} type="button"
                  style={S.cell(sel,tod,dis,hov)}
                  onMouseEnter={()=>!dis&&setHovDay(i)}
                  onMouseLeave={()=>setHovDay(null)}
                  onClick={()=>selectDay(day)}
                  disabled={dis}
                >{day}</button>
              );
            })}
          </div>
        </>}

        {/* MONTHS */}
        {mode === "months" && <>
          <div style={S.headerSub}>
            <button type="button" style={S.backBtn(hovBack)}
              onMouseEnter={()=>setHovBack(true)} onMouseLeave={()=>setHovBack(false)}
              onClick={()=>setMode("days")}>
              <IconChevronLeft style={{width:13,height:13}} /><span>Back</span>
            </button>
            <button type="button" style={{...S.pill(hovYrPill),marginLeft:"auto"}}
              onMouseEnter={()=>setHovYrPill(true)} onMouseLeave={()=>setHovYrPill(false)}
              onClick={()=>setMode("years")}>
              {viewDate.year} <span style={{fontSize:8,opacity:.45}}>▾</span>
            </button>
          </div>
          <div style={S.monthGrid}>
            {MONTHS_SHORT.map((m,i) => {
              const sel = viewDate.month === i;
              const hov = hovMonth === i;
              return (
                <button key={m} type="button"
                  style={S.monthCell(sel, hov&&!sel)}
                  onMouseEnter={()=>setHovMonth(i)}
                  onMouseLeave={()=>setHovMonth(null)}
                  onClick={()=>{ setViewDate(v=>({...v,month:i})); setMode("days"); }}
                >{m}</button>
              );
            })}
          </div>
        </>}

        {/* YEARS */}
        {mode === "years" && <>
          <div style={S.headerSub}>
            <button type="button" style={S.backBtn(hovBack)}
              onMouseEnter={()=>setHovBack(true)} onMouseLeave={()=>setHovBack(false)}
              onClick={()=>setMode("days")}>
              <IconChevronLeft style={{width:13,height:13}} /><span>Back</span>
            </button>
            <span style={S.subLabel}>Select Year</span>
          </div>
          <div style={S.yearList} ref={yearListRef}>
            {yearList.map(y => {
              const sel = viewDate.year === y;
              const hov = hovYear === y;
              return (
                <button key={y} type="button"
                  data-selected={sel}
                  style={S.yearItem(sel, hov&&!sel)}
                  onMouseEnter={()=>setHovYear(y)}
                  onMouseLeave={()=>setHovYear(null)}
                  onClick={()=>{ setViewDate(v=>({...v,year:y})); setMode("days"); }}
                >{y}</button>
              );
            })}
          </div>
        </>}

      </div>
    </>,
    document.body
  );

  /* ── Trigger input ── */
  const triggerStyle = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "8px 11px",
    border: `1.5px solid ${hasError ? T.error : open ? T.teal : T.border}`,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 400,
    color: T.dk,
    background: T.bg,
    boxSizing: "border-box",
    cursor: "text",
    boxShadow: hasError
      ? "0 0 0 3px rgba(224,82,82,0.14)"
      : open
      ? `0 0 0 3px ${T.tealGlow}`
      : "none",
    transition: "border-color 0.18s, box-shadow 0.18s",
    minHeight: 38,
  };

  return (
    <div ref={wrapRef} style={{ width:"100%", position:"relative" }}>
      <div style={triggerStyle} onClick={() => inputRef.current?.focus()}>

        {/* Calendar icon */}
        <span
          onClick={(e) => { e.stopPropagation(); if(open){setOpen(false);setMode("days");}else openCal(); }}
          style={{ display:"flex", alignItems:"center", color: hasError ? T.error : open ? T.teal : T.muted, flexShrink:0, cursor:"pointer", transition:"color 0.18s" }}
        >
          <IconCal />
        </span>

        {/* Typable input */}
        <input
          ref={inputRef}
          type="text"
          placeholder="MM/DD/YYYY"
          value={toDisplay(digits)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onFocus={openCal}
          maxLength={10}
          inputMode="numeric"
          autoComplete="off"
          style={{
            flex:1, border:"none", outline:"none", background:"transparent",
            fontSize:13, fontFamily:"inherit", color:T.dk,
            padding:0, minWidth:0, cursor:"text",
          }}
        />

        {/* Clear button */}
        {digits.length > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setDigits(""); onChange(""); setOpen(false); setMode("days"); }}
            title="Clear"
            style={{
              display:"flex", alignItems:"center", justifyContent:"center",
              width:16, height:16, minWidth:16, borderRadius:"50%",
              border:"none", background:"none", cursor:"pointer",
              flexShrink:0, padding:0, lineHeight:1,
              color:T.muted, fontSize:14, fontFamily:"Arial, sans-serif",
            }}
            onMouseEnter={e => { e.currentTarget.style.color=T.coral; e.currentTarget.style.background="rgba(224,123,84,0.1)"; }}
            onMouseLeave={e => { e.currentTarget.style.color=T.muted; e.currentTarget.style.background="none"; }}
          >&#x2715;</button>
        )}
      </div>

      {dropdown}
    </div>
  );
};

export default DatePicker;